import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CalendarIcon, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { pluralUnit } from "./unitLabel";
import { toIsoDate, isoToDate, formatMDY } from "./dateFormat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_ITEM_VALUE = "__new__";
const UNIT_OPTIONS = ["bag", "can", "case", "box", "jar", "bottle", "other"];

// One invoice line still needing a human decision — Skip / Match Existing / Add to Master,
// and a quantity/date box if the AI couldn't read either off the receipt.
const InvoiceLineReviewRow = ({ invoiceId, line, lineIndex, activeItems, storageAreas, onResolved }) => {
  const [choice, setChoice] = useState(line.suggestedItemId || NEW_ITEM_VALUE);
  const [newName, setNewName] = useState(line.rawText);
  const [quantity, setQuantity] = useState(line.quantity ?? "");
  const [date, setDate] = useState(line.date || toIsoDate(new Date()));
  const [unitType, setUnitType] = useState("bag");
  const [storageArea, setStorageArea] = useState(
    line.isLikelyNonFood ? "supplies" : (storageAreas?.[0]?.key || "unassigned")
  );
  const [busy, setBusy] = useState(false);

  const reviewReason = !line.matchedItemId && line.suggestedItemId == null && line.quantity != null && line.date != null
    ? "No matching item in the Master Item List"
    : null;

  const resolve = async (action) => {
    setBusy(true);
    try {
      const payload = { lineIndex, action };
      if (action === "match_existing") payload.itemId = choice;
      if (action === "add_to_master") {
        payload.canonicalName = newName.trim();
        payload.unitType = unitType;
        payload.storageArea = storageArea;
      }
      if (line.quantity == null) payload.quantity = parseFloat(quantity);
      if (!line.date) payload.date = date;
      const res = await axios.put(`${API}/inventory/invoices/${invoiceId}/resolve`, payload);
      toast.success(action === "skip" ? "Line skipped" : "Posted as a dated stock addition");
      onResolved(res.data.invoice);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resolve this line");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`invoice-line-review-${invoiceId}-${lineIndex}`}>
      <div className="min-w-[120px]">
        <span className="text-sm font-medium text-slate-800 block">{line.rawText}</span>
        <span className="text-[11px] text-amber-700 block" data-testid={`invoice-line-reason-${invoiceId}-${lineIndex}`}>
          {line.quantity == null && line.date == null && "Quantity and date unreadable"}
          {line.quantity == null && line.date != null && "Quantity unreadable"}
          {line.quantity != null && line.date == null && "Date unreadable"}
          {line.quantity != null && line.date != null && (reviewReason || "Needs review")}
        </span>
      </div>
      {line.quantity == null && (
        <div>
          <span className="text-[10px] text-amber-700 block mb-0.5">Quantity unreadable — enter it</span>
          <Input type="number" min="0" placeholder="Quantity" value={quantity}
            data-testid={`invoice-line-quantity-${invoiceId}-${lineIndex}`}
            onChange={(e) => setQuantity(e.target.value)} className="w-24 h-8" />
        </div>
      )}
      {!line.date && (
        <div>
          <span className="text-[10px] text-amber-700 block mb-0.5">Date unreadable — enter it</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-28 justify-start font-normal" data-testid={`invoice-line-date-${invoiceId}-${lineIndex}`}>
                <CalendarIcon className="w-3 h-3 mr-1 text-slate-400" /> {formatMDY(date)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={isoToDate(date)} onSelect={(d) => d && setDate(toIsoDate(d))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      )}
      <Select value={choice} onValueChange={(v) => { setChoice(v); if (v !== NEW_ITEM_VALUE) setNewName(""); }}>
        <SelectTrigger className="w-48 h-8" data-testid={`invoice-line-item-select-${invoiceId}-${lineIndex}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NEW_ITEM_VALUE}>+ Add to Master as new item</SelectItem>
          {activeItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.canonicalName}</SelectItem>)}
        </SelectContent>
      </Select>
      {choice === NEW_ITEM_VALUE && (
        <>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-40 h-8"
            data-testid={`invoice-line-new-name-${invoiceId}-${lineIndex}`} placeholder="New item name" />
          <Select value={unitType} onValueChange={setUnitType}>
            <SelectTrigger className="w-24 h-8" data-testid={`invoice-line-unit-select-${invoiceId}-${lineIndex}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={storageArea} onValueChange={setStorageArea}>
            <SelectTrigger className="w-32 h-8" data-testid={`invoice-line-storage-select-${invoiceId}-${lineIndex}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {(storageAreas || []).map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </>
      )}
      <Button size="sm" className="h-8" disabled={busy} data-testid={`invoice-line-confirm-${invoiceId}-${lineIndex}`}
        onClick={() => resolve(choice === NEW_ITEM_VALUE ? "add_to_master" : "match_existing")}>
        Post
      </Button>
      <Button size="sm" variant="outline" className="h-8" disabled={busy} data-testid={`invoice-line-skip-${invoiceId}-${lineIndex}`}
        onClick={() => resolve("skip")}>
        Skip
      </Button>
    </div>
  );
};

const LINE_STATUS_META = {
  auto_posted: { label: "Posted automatically", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  resolved: { label: "Posted", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  skipped: { label: "Skipped", className: "bg-slate-200 text-slate-600", Icon: AlertTriangle },
  needs_review: { label: "Needs review", className: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
};

export const InvoicesReceiptsPanel = ({ month, year, authHeaders, items, storageAreas, onPurchaseAdded }) => {
  const [purchases, setPurchases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState(toIsoDate(new Date()));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const itemsById = Object.fromEntries((items || []).map((i) => [i.id, i]));
  const activeItems = (items || []).filter((i) => i.active);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [purchasesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/inventory/purchases`, { params: { month, year }, headers: authHeaders }),
        axios.get(`${API}/inventory/invoices`, { params: { month, year }, headers: authHeaders }),
      ]);
      setPurchases(purchasesRes.data.purchases || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (err) {
      console.error("Error fetching invoices/purchases:", err);
    } finally {
      setLoading(false);
    }
  }, [month, year, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAddStock = async () => {
    if (!itemId || !quantity || !date) {
      toast.error("Choose an item, quantity, and date");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/inventory/purchases`, {
        itemId, quantity: parseFloat(quantity), date,
      }, { headers: authHeaders });
      if (res.data.purchase?.possibleDuplicate) {
        toast.warning("Added — but this looks similar to another purchase logged within a few days. Check the ledger below if that wasn't intentional.");
      } else {
        toast.success("Stock addition logged");
      }
      setQuantity(""); setAdding(false);
      await fetchAll();
      onPurchaseAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log stock addition");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/inventory/invoices/upload`, formData, {
        params: { month, year }, headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.summary || "Receipt uploaded");
      await fetchAll();
      onPurchaseAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploading(false);
    }
  };

  const handleLineResolved = async (updatedInvoice) => {
    setInvoices((prev) => prev.map((inv) => (inv.id === updatedInvoice.id ? updatedInvoice : inv)));
    await fetchAll();
    onPurchaseAdded?.();
  };

  return (
    <div data-testid="invoices-receipts-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500">
          Dated stock additions — manual entries and invoice/receipt uploads. Never changes your physical count;
          the count you save in Take Inventory is still what's actually there.
        </p>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden"
            data-testid="upload-invoice-file-input" onChange={handleUploadFile} />
          <Button size="sm" variant="outline" data-testid="upload-invoice-button" disabled={uploading}
            onClick={() => fileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-1" /> {uploading ? "Reading…" : "Upload Invoice/Receipt"}
          </Button>
          <Button size="sm" data-testid="add-stock-button" onClick={() => setAdding((v) => !v)}>
            <Plus className="w-4 h-4 mr-1" /> Add Stock
          </Button>
        </div>
      </div>

      {adding && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <Select value={itemId} onValueChange={setItemId}>
            <SelectTrigger className="w-56" data-testid="add-stock-item-select"><SelectValue placeholder="Item" /></SelectTrigger>
            <SelectContent>
              {activeItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.canonicalName}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="add-stock-quantity-input" type="number" min="0" placeholder="Quantity" value={quantity}
            onChange={(e) => setQuantity(e.target.value)} className="w-28" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-32 justify-start font-normal" data-testid="add-stock-date-input">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" /> {formatMDY(date)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={isoToDate(date)} onSelect={(d) => d && setDate(toIsoDate(d))} initialFocus />
            </PopoverContent>
          </Popover>
          <Button size="sm" data-testid="confirm-add-stock-button" disabled={saving} onClick={handleAddStock}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button size="sm" variant="outline" data-testid="cancel-add-stock-button" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      {!loading && invoices.some((inv) => inv.lines.some((l) => l.status === "needs_review")) && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Needs Review</p>
          {invoices.map((inv) => inv.lines.map((line, idx) => line.status === "needs_review" && (
            <InvoiceLineReviewRow key={`${inv.id}-${idx}`} invoiceId={inv.id} line={line} lineIndex={idx}
              activeItems={activeItems} storageAreas={storageAreas} onResolved={handleLineResolved} />
          )))}
        </div>
      )}

      {!loading && invoices.length > 0 && (
        <div className="space-y-1" data-testid="invoices-list">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded Receipts</p>
          {invoices.map((inv) => (
            <div key={inv.id} className="text-xs text-slate-500 flex flex-wrap items-center gap-2 py-1" data-testid={`invoice-summary-${inv.id}`}>
              <span className="font-medium text-slate-700">{inv.filename}</span>
              <span>uploaded by {inv.uploadedBy}, {formatMDY(inv.uploadedAt)}</span>
              {inv.lines.map((line, idx) => {
                const meta = LINE_STATUS_META[line.status];
                if (!meta) return null;
                const Icon = meta.Icon;
                return (
                  <span key={idx} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${meta.className}`}
                    title={meta.label} data-testid={`invoice-line-chip-${inv.id}-${idx}`}>
                    <Icon className="w-3 h-3" /> {line.rawText} — {meta.label}
                    {line.possibleDuplicate && (
                      <span data-testid={`invoice-possible-duplicate-${inv.id}-${idx}`}> (possible duplicate)</span>
                    )}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Loading...</p>
      ) : purchases.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-purchases-message">
          No stock additions logged yet this month.
        </p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="purchases-ledger-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Quantity</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Entered By</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} data-testid={`purchase-row-${p.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2" data-testid={`purchase-date-${p.id}`}>{formatMDY(p.date)}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{p.itemName}</td>
                  <td className="px-3 py-2">{p.quantity} {pluralUnit(itemsById[p.itemId]?.unitType || "bag", p.quantity)}</td>
                  <td className="px-3 py-2">
                    <span className="capitalize">{p.source}</span>
                    {p.possibleDuplicate && (
                      <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`possible-duplicate-${p.id}`}>
                        Possible duplicate
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{p.enteredBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoicesReceiptsPanel;
